// Shared attention-banner constants (marketplace "attention banner" corner
// chip, operator-approved design 2026-08-16).
//
// Canonical home: embed-shared is the ONE root both consumers already import
// from — the portal's API layer imports embed-shared/types (see
// portal/api/marketplace.ts) and every embed component imports
// embed-shared/* — so the preset map lives here exactly once (§47 "one
// canonical X everywhere": duplicating the literal map inside portal is
// forbidden; re-export from embed/marketplace/attention-banner.tsx instead).

/** One operator preset: stable id + displayed label + default chip color. */
export interface AttentionPreset {
  id: string;
  label: string;
  color: string;
}

/**
 * Max banner label length in RUNES. MUST stay in lockstep with the
 * server-side validation (label trimmed, non-empty, ≤ 30 runes) — tests
 * reference this constant, never a hardcoded 30 (§55).
 */
export const ATTENTION_LABEL_MAX = 30;

/**
 * Server-side `attention_color` validation, mirrored client-side: trimmed,
 * exactly `#` + 6 hex digits. Shared so the portal's color input and the
 * embed chip's fallback decision cannot drift from the wire contract.
 */
export const ATTENTION_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/** True when `color` is a valid `#RRGGBB` string (trimmed before testing,
 *  matching the server's trim-then-validate order). */
export function isValidAttentionColor(color: string | null | undefined): boolean {
  if (typeof color !== "string") return false;
  return ATTENTION_COLOR_PATTERN.test(color.trim());
}

/**
 * The four operator presets. Colors come from the storefront's existing
 * palette family so the chips read native in both themes; all four sit
 * below the chip's luminance threshold (white text). Operators may also use
 * custom text + custom color — presets are defaults, not a closed set.
 */
export const ATTENTION_PRESETS: readonly AttentionPreset[] = [
  { id: "new-arrival", label: "New Arrival", color: "#1D59FF" }, // brand blue (LIGHT primary)
  { id: "popular", label: "Popular", color: "#16A34A" }, // success green
  { id: "limited-offer", label: "Limited Offer", color: "#D97706" }, // amber
  { id: "reduced-price", label: "Reduced Price", color: "#DC2626" }, // red
];
